// Design agent: Spec 007 — surfaces how much of the VLSM supernet is still available after method
// 1 lays out its subnets, so an operator immediately sees the addresses left, the usage percentage,
// and which aligned CIDR blocks could still be carved out of the remaining gaps ("still fits").
// Developer agent: Purely presentational. VlsmCalculator already computed the address accounting
// (allocated/remaining/free blocks) inside its methodOneResults memo; this component only groups the
// free blocks for display and renders them, so the maths stays in one place (ipMath.js) and this
// file stays about layout.
import { useState } from 'react';
import { intToIpv4 } from '../utils/ipMath';
import './PoolUsage.css';

// Design agent: Groups the raw list of aligned free blocks by prefix length so the "still fits"
// summary reads as "1 × /25, 2 × /26" instead of one line per individual block.
// Developer agent: decomposeFreeSpace already emits blocks from the lowest address to the highest,
// so grouping preserves that order via the Map's insertion order before the final numeric sort.
const groupFreeBlocksByPrefix = (freeBlocks) => {
  const countByPrefix = new Map();
  freeBlocks.forEach((block) => {
    countByPrefix.set(block.prefix, (countByPrefix.get(block.prefix) ?? 0) + 1);
  });
  return Array.from(countByPrefix.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([prefix, count]) => ({ prefix, count }));
};

// Design agent: Implements the VLSM pool usage summary panel described in spec 007: supernet size,
// allocated/remaining addresses, a usage progress bar, the "still fits" decomposition, and an
// over-allocation alert when the requested subnets could not all be placed.
function PoolUsage({
  id,
  networkCapacity,
  allocatedAddresses,
  remainingAddresses,
  usagePercent,
  freeBlocks,
  overflowAddresses,
}) {
  // Design agent: The grouped summary is the default view; the exact network address of every free
  // block is one click away instead of always shown, keeping the panel compact.
  const [isExpanded, setIsExpanded] = useState(false);

  const groupedFreeBlocks = groupFreeBlocksByPrefix(freeBlocks);
  const stillFitsSummary =
    groupedFreeBlocks.length === 0
      ? 'Pool fully used'
      : groupedFreeBlocks.map((group) => `${group.count} × /${group.prefix}`).join(', ');

  // Design agent: Clamp defensively so a rounding edge case never renders a bar over/under 100%.
  const clampedPercent = Math.max(0, Math.min(100, usagePercent));
  // Design agent: Reuses the exact orange→red host gradient introduced in spec 001 so every gauge
  // in the app reads the same way, instead of inventing a second colour language for "usage".
  const progressGradient = `linear-gradient(90deg, #fdba74 0%, #ea580c ${clampedPercent}%, rgba(255, 255, 255, 0.08) ${clampedPercent}%, rgba(255, 255, 255, 0.08) 100%)`;

  // Developer agent: Toggles the free-block address list open/closed.
  const toggleExpanded = () => setIsExpanded((previous) => !previous);

  return (
    <div className="pool-usage" id={id}>
      <div className="result-subheading" id={`${id}-heading`}>
        Pool usage
      </div>

      {overflowAddresses > 0 && (
        <p className="error-text" id={`${id}-overflow-alert`}>
          Exceeds pool by {overflowAddresses.toLocaleString()} addresses
        </p>
      )}

      <div className="result-metric-grid" id={`${id}-metrics`}>
        <div className="result-metric" id={`${id}-metric-supernet`}>
          <span className="result-metric-label">Supernet size</span>
          <span className="result-metric-value">{networkCapacity.toLocaleString()} addrs</span>
        </div>
        <div className="result-metric" id={`${id}-metric-allocated`}>
          <span className="result-metric-label">Allocated</span>
          <span className="result-metric-value">{allocatedAddresses.toLocaleString()} addrs</span>
        </div>
        <div className="result-metric" id={`${id}-metric-remaining`}>
          <span className="result-metric-label">Remaining</span>
          <span className="result-metric-value">{remainingAddresses.toLocaleString()} addrs</span>
        </div>
      </div>

      {/* Design agent: Bug fix — the percentage label used to be a sibling *inside* the 14px-tall,
          overflow:hidden `.pool-usage-progress` track (with a margin-top meant to push it below the
          bar), but overflow:hidden clipped it before that margin could take effect, so at a small
          usage percentage the label rendered squashed into the thin fill sliver and visibly
          overlapped the bar's bottom edge. It now lives in its own row above the track — always
          fully outside the clipped element, at every percentage from 0% to 100% — right-aligned so
          it reads like a caption for the bar directly beneath it. */}
      <div className="pool-usage-progress-row" id={`${id}-progress-row`}>
        <span className="pool-usage-progress-label" id={`${id}-progress-label`}>
          {clampedPercent.toFixed(1)}% used
        </span>
        <div className="pool-usage-progress" id={`${id}-progress-track`}>
          <div
            className="pool-usage-progress-fill"
            id={`${id}-progress-fill`}
            style={{ background: progressGradient }}
          />
        </div>
      </div>

      <div className="pool-usage-still-fits" id={`${id}-still-fits`}>
        <span className="result-metric-label">Still fits</span>
        <span className="pool-usage-still-fits-value" id={`${id}-still-fits-value`}>
          {stillFitsSummary}
        </span>
        {groupedFreeBlocks.length > 0 && (
          <button
            type="button"
            className="ghost-button expand-button"
            id={`${id}-expand-button`}
            onClick={toggleExpanded}
          >
            {isExpanded ? 'Hide free block addresses' : 'Show free block addresses'}
          </button>
        )}
      </div>

      {isExpanded && groupedFreeBlocks.length > 0 && (
        <ul className="pool-usage-free-list" id={`${id}-free-list`}>
          {freeBlocks.map((block, index) => (
            <li
              key={`${block.network}-${block.prefix}`}
              className="pool-usage-free-item"
              id={`${id}-free-item-${index}`}
            >
              {intToIpv4(block.network)}/{block.prefix}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PoolUsage;
export { PoolUsage };
